import React, { useEffect, useRef, useState, forwardRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Tooltip,
} from '@mui/material';
import { Order } from '../types/Order';
import { ORDERS_TABLE_CONSTANTS } from '../constants/ordersTable';
import { formatCurrency, formatDateTime, getStateColor, getFlowColor, truncateText } from '../utils/orderUtils';

interface OrdersTableProps {
  orders: Order[];
  onOrdersPerPageChange?: (ordersPerPage: number) => void;
}

const OrdersTable = forwardRef<HTMLDivElement, OrdersTableProps>(({
  orders,
  onOrdersPerPageChange,
}, ref) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [calculatedOrdersPerPage, setCalculatedOrdersPerPage] = useState(10);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const lastCalculatedHeightRef = useRef<number>(0);

  const calculateOrdersPerPage = useCallback(() => {
    if (!tableContainerRef.current) return 10;

    const containerHeight = tableContainerRef.current.clientHeight;
    
    const THRESHOLD = 5; 
    if (Math.abs(containerHeight - lastCalculatedHeightRef.current) < THRESHOLD) {
      return calculatedOrdersPerPage;
    }
    
    lastCalculatedHeightRef.current = containerHeight;
    
    const availableHeight = containerHeight 
      - ORDERS_TABLE_CONSTANTS.HEADER_HEIGHT 
      - ORDERS_TABLE_CONSTANTS.PADDING_BUFFER;
    
    const calculatedRows = Math.floor((availableHeight - 2) / ORDERS_TABLE_CONSTANTS.ROW_HEIGHT);
    
    return Math.max(ORDERS_TABLE_CONSTANTS.MIN_ROWS, calculatedRows);
  }, [calculatedOrdersPerPage]);

  const updateOrdersPerPage = useCallback(() => {
    const newOrdersPerPage = calculateOrdersPerPage();
    
    if (Math.abs(newOrdersPerPage - calculatedOrdersPerPage) >= 1) {
      setCalculatedOrdersPerPage(newOrdersPerPage);
      onOrdersPerPageChange?.(newOrdersPerPage);
    }
  }, [calculateOrdersPerPage, calculatedOrdersPerPage, onOrdersPerPageChange]);

  // Initial calculation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateOrdersPerPage();
    }, ORDERS_TABLE_CONSTANTS.INITIAL_CALCULATION_DELAY);

    return () => clearTimeout(timeoutId);
  }, []); 

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      resizeTimeoutRef.current = setTimeout(() => {
        updateOrdersPerPage();
      }, ORDERS_TABLE_CONSTANTS.RESIZE_DEBOUNCE_DELAY || 300);
    };

    window.addEventListener('resize', handleResize);
    
    let resizeObserver: ResizeObserver | null = null;
    
    if (tableContainerRef.current) {
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.height > 0) {
            handleResize();
          }
        }
      });
      
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [updateOrdersPerPage]);

  return (
    <TableContainer 
      ref={tableContainerRef}
      component={Paper} 
      sx={{ 
        flex: 1, 
        boxShadow: 'none',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'auto',
        scrollbarGutter: 'stable'
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ height: ORDERS_TABLE_CONSTANTS.HEADER_HEIGHT }}>
            <TableCell><strong>Id</strong></TableCell>
            <TableCell><strong>Flow</strong></TableCell>
            <TableCell><strong>State</strong></TableCell>
            <TableCell align="right"><strong>Budget</strong></TableCell>
            <TableCell align="right"><strong>Tip</strong></TableCell>
            <TableCell><strong>Title</strong></TableCell>
            <TableCell><strong>Brief</strong></TableCell>
            <TableCell><strong>Description</strong></TableCell>
            <TableCell><strong>Fulfilled By</strong></TableCell>
            <TableCell><strong>Fulfilled Timestamp</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow 
              key={order.id} 
              hover
              sx={{ 
                height: ORDERS_TABLE_CONSTANTS.ROW_HEIGHT,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <TableCell>{order.id}</TableCell>
              <TableCell>
                <Chip
                  label={order.flowId}
                  size="small"
                  sx={{
                    bgcolor: getFlowColor(order.flowId),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={order.flowState}
                  size="small"
                  sx={{
                    bgcolor: getStateColor(order.flowState),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  ${formatCurrency(order.budget)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {order.tip || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={order.title || ''} arrow>
                  <Typography variant="body2" sx={{ 
                    maxWidth: 150, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer'
                  }}>
                    {truncateText(order.title, 20)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={order.brief || ''} arrow>
                  <Typography variant="body2" sx={{ 
                    maxWidth: 150, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer'
                  }}>
                    {truncateText(order.brief, 20)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={order.description || ''} arrow>
                  <Typography variant="body2" sx={{ 
                    maxWidth: 200, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer'
                  }}>
                    {truncateText(order.description, 30)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {order.fulfilledById || '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(order.fulfilledTimestamp)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          {orders.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  {ORDERS_TABLE_CONSTANTS.NO_ORDERS_MESSAGE}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

OrdersTable.displayName = 'OrdersTable';

export default OrdersTable;